<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="500px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template>
                        {{ dialogTitle }}
                    </template>
                </h1>
            </v-card-title>

            <v-card-text class="text-center">
                <v-container fluid class="pa-0">
                    <v-row>
                        <v-col v-if="labelFromShipmentsOrder" cols="12">
                            <v-autocomplete
                                v-model="shippingOrder"
                                label="Numer zlecenia spedycyjnego"
                                :items="shippingOrdersFound"
                                :search-input.sync="shippingOrderSearchQuery"
                                :filter="customFilter"
                                :loading="actionLoading.search"
                                :error-messages="shippingOrderErrors"
                                no-data-text="Nie znaleziono"
                                clearable
                                dense
                                outlined
                                @click:clear="clearShipmentsOrderSearch"
                                @change="getShipmentsOrderFromSearchedData"
                            >
                                <template slot="selection" slot-scope="data">
                                    {{ data.item.number }}
                                </template>

                                <template slot="item" slot-scope="data">
                                    {{ data.item.number }}
                                </template>

                                <template slot="no-data">
                                    <v-list-item>
                                        <v-list-item-title>
                                            <template v-if="!shippingOrderSearchQuery">
                                                Wpisz treść aby odnaleźć <strong>zlecenie spedycyjne</strong>.
                                            </template>
                                            <template v-else-if="shippingOrderSearchQuery.length < searchQueryMinLength">
                                                Wpisz przynajmniej <strong>{{ searchQueryMinLength }}</strong> znaki.
                                            </template>
                                            <template v-else-if="actionLoading.search">
                                                Trwa wyszukiwanie zleceń...
                                            </template>
                                            <template v-else>
                                                Nie odnaleziono zleceń dla zapytania: "<strong> {{ shippingOrderSearchQuery }}".</strong>
                                            </template>
                                        </v-list-item-title>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>
                        </v-col>
                        <v-col v-else cols="12">
                            <v-autocomplete
                                v-model="eCommerceOrder"
                                label="Numer zamówienia"
                                :items="eCommerceOrdersFound"
                                :search-input.sync="eCommerceOrderSearchQuery"
                                :filter="customFilter"
                                :loading="actionLoading.search"
                                :error-messages="eCommerceOrderErrors"
                                no-data-text="Nie znaleziono"
                                hint="Wpisz lub zeskanuj numer zamówienia"
                                persistent-hint
                                clearable
                                dense
                                outlined
                                autofocus
                                @click:clear="clearECommerceOrderSearch"
                                @change="getECommerceOrderFromSearchedData"
                            >
                                <template slot="selection" slot-scope="data">
                                    {{ data.item.productionNumber }}
                                </template>

                                <template slot="item" slot-scope="data">
                                    {{ data.item.productionNumber }}
                                </template>

                                <template slot="no-data">
                                    <v-list-item>
                                        <v-list-item-title>
                                            <template v-if="!eCommerceOrderSearchQuery">
                                                Wpisz treść aby odnaleźć <strong>zamówienie</strong>.
                                            </template>
                                            <template v-else-if="eCommerceOrderSearchQuery.length < searchQueryMinLength">
                                                Wpisz przynajmniej <strong>{{ searchQueryMinLength }}</strong> znaki.
                                            </template>
                                            <template v-else-if="actionLoading.search">
                                                Trwa wyszukiwanie zamówień...
                                            </template>
                                            <template v-else>
                                                Nie odnaleziono zamówień dla zapytania: "<strong> {{ eCommerceOrderSearchQuery }}".</strong>
                                            </template>
                                        </v-list-item-title>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    data-sentry-label="Utwórz"
                    @click="onSave"
                >
                    Utwórz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import decodeQRcode from '@/mixins/decodeQRcode'
import { requiredIf, requiredUnless } from 'vuelidate/lib/validators'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

let searchTimer

export default {
    name: 'ImportShippingLabel',
    mixins: [
        decodeQRcode
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        createFrom: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            searchQueryMinLength: 3,
            actionLoading: {
                search: false
            },
            shippingOrdersFound: [],
            shippingOrderSearchQuery: null,
            shippingOrder: {},
            shippingOrderId: null,
            eCommerceOrdersFound: [],
            eCommerceOrderSearchQuery: null,
            eCommerceOrder: {},
            eCommerceOrderId: null
        }
    },
    validations() {
        return {
            shippingOrder: {
                required: requiredIf(function() {
                    return this.labelFromShipmentsOrder
                })
            },
            eCommerceOrder: {
                required: requiredUnless(function() {
                    return this.labelFromShipmentsOrder
                })
            }
        }
    },
    computed: {
        dialogTitle() {
            let ending = ' z zamówienia'

            if (this.labelFromShipmentsOrder) {
                ending = ' ze zlecania spedycyjnego'
            }

            return `Tworzenie zawieszki ${ending}`
        },
        labelFromShipmentsOrder() {
            return this.createFrom === 'ShipmentsOrder'
        },
        shippingOrderErrors() {
            const errors = []

            if (!this.$v.shippingOrder.$dirty) return errors
            !this.$v.shippingOrder.required && errors.push('Numer zlecenia jest wymagany.')

            return errors
        },
        eCommerceOrderErrors() {
            const errors = []

            if (!this.$v.eCommerceOrder.$dirty) return errors
            !this.$v.eCommerceOrder.required && errors.push('Numer zamówienia jest wymagany.')

            return errors
        }
    },
    watch: {
        shippingOrderSearchQuery(value = null) {
            if (value === null || value.length < this.searchQueryMinLength) return

            this.actionLoading.search = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchShipmentsOrders(value)
                        .catch(error => {
                            errorHandler(error)
                        })
                } else {
                    this.actionLoading.search = false
                    this.shippingOrdersFound = []
                }
            }, 500)
        },
        eCommerceOrderSearchQuery(value = null) {
            if (value === null || value.length < this.searchQueryMinLength) return

            this.actionLoading.search = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchECommerceOrders(value)
                        .catch(error => {
                            errorHandler(error)
                        })
                } else {
                    this.actionLoading.search = false
                    this.eCommerceOrdersFound = []
                }
            }, 500)
        }
    },
    created() {
        document.addEventListener('keydown', this.QRtoJSON)
    },
    destroyed() {
        document.removeEventListener('keydown', this.QRtoJSON)
    },
    methods: {
        onSave() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }


            if (this.labelFromShipmentsOrder) {
                this.$emit('on-submit', this.shippingOrderId)
            } else {
                this.$emit('on-submit', this.eCommerceOrderId)
            }
            this.onClose()
        },
        onClose() {
            setTimeout(() => {
                if (this.labelFromShipmentsOrder) {
                    this.shippingOrder = null
                    this.shippingOrderId = null
                    this.clearShipmentsOrderSearch()
                } else {
                    this.eCommerceOrder = null
                    this.eCommerceOrderId = null
                    this.clearECommerceOrderSearch()
                }
            }, 500)
            this.$v.$reset()

            this.$emit('on-close')
        },
        clearShipmentsOrderSearch() {
            this.shippingOrderSearchQuery = null
            this.shippingOrdersFound = []
        },
        clearECommerceOrderSearch() {
            this.eCommerceOrderSearchQuery = null
            this.eCommerceOrdersFound = []
        },
        customFilter(item, queryText) {
            let order

            if (this.labelFromShipmentsOrder) {
                order = item.number.toLowerCase()
            } else {
                order = item.productionNumber.toLowerCase()
            }
            const search = queryText.toLowerCase()

            return order.indexOf(search) > -1
        },
        getShipmentsOrderFromSearchedData(shippingOrder) {
            if (!shippingOrder) return
            this.shippingOrderId = shippingOrder.id
        },
        getECommerceOrderFromSearchedData(eCommerceOrder) {
            if (!eCommerceOrder) return
            this.eCommerceOrderId = eCommerceOrder.id
        },
        async searchShipmentsOrders(number = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie zlecenia spedycyjnego')

            return await this.$store.dispatch('shipments/shipmentsOrders/getShippingOrdersForShippingLabels', {
                paginate: '',
                query: `?number=${number}`,
            })
                .then(response => {
                    this.shippingOrdersFound = response.data.results

                    return response.data.results
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.actionLoading.search = false
                })
        },
        async searchECommerceOrders(number = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie zamówienia')

            return await this.$store.dispatch('orders/getOrdersForShippingLabels', {
                paginate: '?limit=100',
                query: `&productionNumber=${number}`,
            })
                .then(response => {
                    this.eCommerceOrdersFound = response.data.results

                    return response.data.results
                })
                .catch(error => {
                    throw error
                })
                .finally(() => {
                    this.actionLoading.search = false
                })
        },
        handleQrJSON({ data }) {
            if (data.type === 'KP') {
                this.scannerEnterFallback(() => {
                    const regex = /(\w+)_(\w+)_(\w+)_(\w)/

                    const productionECommerceOrderNumber = data.order.replace(regex, '$1_$2_$4')

                    this.searchECommerceOrders(productionECommerceOrderNumber).then(response => {
                        if (response.length === 0) {
                            this.$store.dispatch('showNotification', {
                                type: 'error',
                                message: 'Nie znaleziono zamówienia.'
                            })

                            return
                        }

                        this.eCommerceOrder = response[0]
                        this.getECommerceOrderFromSearchedData(this.eCommerceOrder)
                        this.onSave()
                    })
                })
            }
            else {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('reports.messages.unsupportedQrCode')
                })
            }
        },
    }
}
</script>
